import { ThemedText } from '@/components/themed-text';
import { FREE_PLAN, PROSE } from '@/content/facts';
import { Button } from '@/components/ui/button';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link } from 'expo-router';
import React, { useEffect, useRef } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';

/** Curva única de movimento das páginas públicas (massa + mola). */
export const SEO_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

export type IoniconName = keyof typeof Ionicons.glyphMap;

/**
 * Entrada suave ao rolar, só na web. O HTML estático sai visível: o bloco só é
 * escondido depois de montar e se ainda estiver abaixo da dobra, então buscador
 * e primeira pintura nunca veem opacidade 0.
 */
export function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof IntersectionObserver === 'undefined') return;
    const el = ref.current as unknown as HTMLElement | null;
    if (!el || el.getBoundingClientRect().top < window.innerHeight) return;

    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition = `opacity 900ms ${SEO_EASE} ${delay}ms, transform 900ms ${SEO_EASE} ${delay}ms`;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.style.opacity = '1';
        el.style.transform = 'none';
        io.disconnect();
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  return (
    <View ref={ref} className={className}>
      {children}
    </View>
  );
}

export function Crumbs({ items, center }: { items: { label: string; href?: Href }[]; center?: boolean }) {
  return (
    <View
      accessibilityLabel="Você está em"
      className={`flex-row flex-wrap items-center gap-x-2 gap-y-1 ${center ? 'justify-center' : ''}`}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 ? <Ionicons className="text-neutral-400" name="chevron-forward" size={12} /> : null}
          {item.href ? (
            <Link href={item.href} asChild>
              <Pressable accessibilityRole="link">
                <ThemedText className="text-xs text-neutral-500">{item.label}</ThemedText>
              </Pressable>
            </Link>
          ) : (
            <ThemedText className="text-xs text-neutral-400">{item.label}</ThemedText>
          )}
        </React.Fragment>
      ))}
    </View>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  center,
}: {
  eyebrow?: string;
  title: string;
  center?: boolean;
}) {
  return (
    <Reveal className={`mb-8 ${center ? 'items-center' : ''}`}>
      {eyebrow ? (
        <ThemedText className="font-semibold text-sm text-primary">{eyebrow}</ThemedText>
      ) : null}
      <ThemedText
        role="heading"
        aria-level={2}
        className={`mt-2 text-balance font-bold text-3xl tracking-tight md:text-4xl ${center ? 'text-center' : ''}`}
      >
        {title}
      </ThemedText>
    </Reveal>
  );
}

/** Seção com a largura e o respiro padrão. `tone="white"` alterna o fundo. */
export function SeoSection({
  children,
  tone,
  narrow,
  flushTop,
}: {
  children: React.ReactNode;
  tone?: 'white';
  narrow?: boolean;
  /** Colada no bloco de cima (ex.: logo depois do hero de um guia). */
  flushTop?: boolean;
}) {
  const spacing = flushTop ? 'pb-16 pt-8 md:pb-24 md:pt-10' : 'py-16 md:py-24';
  return (
    <View className={`px-4 md:px-8 ${spacing} ${tone === 'white' ? 'bg-card' : ''}`}>
      <View className={`mx-auto w-full ${narrow ? 'max-w-4xl' : 'max-w-6xl'}`}>{children}</View>
    </View>
  );
}

/** Cartões pequenos de ícone + rótulo curto (dores, recursos, guias). */
export function IconTiles({
  items,
  tone = 'muted',
}: {
  items: { icon: string; title: string; sub?: string; href?: Href }[];
  tone?: 'muted' | 'card';
}) {
  return (
    <View className="flex-row flex-wrap gap-3">
      {items.map((item, index) => {
        const tile = (
          <View
            className={`h-full gap-3 rounded-2xl p-4 md:p-5 ${
              tone === 'card' ? 'border border-neutral-900/5 bg-card' : 'bg-neutral-50'
            }`}
          >
            <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
              <Ionicons className="text-primary dark:text-white" name={item.icon as IoniconName} size={20} />
            </View>
            <ThemedText
              role={item.sub ? 'heading' : undefined}
              aria-level={item.sub ? 3 : undefined}
              className="font-semibold text-sm text-neutral-800 md:text-base"
            >
              {item.title}
            </ThemedText>
            {item.sub ? <ThemedText className="text-sm text-neutral-500">{item.sub}</ThemedText> : null}
          </View>
        );
        return (
          <Reveal key={item.title} delay={index * 60} className="w-[48%] grow basis-[40%] md:basis-[22%]">
            {item.href ? (
              <Link href={item.href} asChild>
                <Pressable accessibilityRole="link" className="h-full">
                  {tile}
                </Pressable>
              </Link>
            ) : (
              tile
            )}
          </Reveal>
        );
      })}
    </View>
  );
}

/** Lista de links em cartões largos (guias, comparações). */
export function LinkList({ items }: { items: { label: string; href: Href }[] }) {
  return (
    <View className="gap-2">
      {items.map((item) => (
        <Link key={String(item.href)} href={item.href} asChild>
          <Pressable
            accessibilityRole="link"
            className="min-h-14 flex-row items-center justify-between gap-3 rounded-2xl border border-neutral-900/5 bg-card px-4 py-3 transition-colors hover:border-primary-200"
          >
            <ThemedText className="flex-1 font-medium text-sm">{item.label}</ThemedText>
            <Ionicons name="arrow-forward" size={18} color="#FD5001" />
          </Pressable>
        </Link>
      ))}
    </View>
  );
}

/** Links curtos em pílula (ramos, cidades). */
export function ChipLinks({ items }: { items: { label: string; href: Href }[] }) {
  return (
    <View className="flex-row flex-wrap gap-2">
      {items.map((item) => (
        <Link key={String(item.href)} href={item.href} asChild>
          <Pressable
            accessibilityRole="link"
            className="h-9 justify-center rounded-full border border-neutral-200 bg-card px-4 transition-colors hover:border-primary-200"
          >
            <ThemedText className="font-medium text-sm text-neutral-700">{item.label}</ThemedText>
          </Pressable>
        </Link>
      ))}
    </View>
  );
}

/** Bloco "veja também": título + listas de links. */
export function SeeAlso({
  links,
  chips,
  title = 'Veja também',
}: {
  links?: { label: string; href: Href }[];
  chips?: { label: string; href: Href }[];
  title?: string;
}) {
  if (!links?.length && !chips?.length) return null;
  return (
    <View className="mt-12 gap-6">
      <ThemedText role="heading" aria-level={2} className="font-semibold text-lg">
        {title}
      </ThemedText>
      {links?.length ? <LinkList items={links} /> : null}
      {chips?.length ? <ChipLinks items={chips} /> : null}
    </View>
  );
}

/**
 * Frase grande no meio da página. Na web, as palavras acendem uma a uma ao
 * entrar na tela; no HTML estático já saem na cor cheia.
 */
export function Tagline({ text }: { text: string }) {
  const ref = useRef<View>(null);
  const words = text.split(' ');

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof IntersectionObserver === 'undefined') return;
    const el = ref.current as unknown as HTMLElement | null;
    if (!el || el.getBoundingClientRect().top < window.innerHeight) return;
    const spans = Array.from(el.querySelectorAll<HTMLElement>('[data-word]'));
    spans.forEach((span, i) => {
      span.style.color = 'rgba(23,23,23,0.2)';
      span.style.transition = `color 700ms ${SEO_EASE} ${i * 90}ms`;
    });
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        spans.forEach((span) => (span.style.color = '#171717'));
        io.disconnect();
      },
      { rootMargin: '0px 0px -35% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <SeoSection tone="white">
      <View ref={ref}>
        <ThemedText className="max-w-3xl text-balance font-bold text-4xl tracking-tight text-neutral-900 md:text-6xl">
          {words.map((word, i) => (
            <Text key={`${word}-${i}`} {...({ dataSet: { word: String(i) } } as object)}>
              {word}
              {i < words.length - 1 ? ' ' : ''}
            </Text>
          ))}
        </ThemedText>
      </View>
    </SeoSection>
  );
}

/** Linha fixa do que é grátis e do que é pago (sai do `facts.ts`). */
export const FREE_VS_PAID = `Na conta grátis: agenda, até ${FREE_PLAN.maxClientsPerMonth} clientes por mês e cobrança avulsa. Link de agendamento, lembrete no WhatsApp, mensalidade e recebimento pelo link (com taxa) nos planos pagos, ${PROSE.fromPrice}.`;

export function PlanLine({ className = '' }: { className?: string }) {
  return (
    <View className={`flex-row items-start gap-2 rounded-2xl bg-neutral-50 p-4 ${className}`}>
      <Ionicons name="information-circle-outline" size={18} color="#FD5001" />
      <ThemedText className="flex-1 text-sm text-neutral-600">{FREE_VS_PAID}</ThemedText>
    </View>
  );
}

/** CTA final, igual ao do topo, com a reversão de risco. */
export function FinalCta({ title }: { title: string }) {
  return (
    <SeoSection>
      <Reveal className="items-center">
        <ThemedText
          role="heading"
          aria-level={2}
          className="mb-8 max-w-2xl text-balance text-center font-bold text-3xl tracking-tight md:text-4xl"
        >
          {title}
        </ThemedText>
        <Link href="/(auth)/register" asChild>
          <Button size="lg" title="Criar conta grátis" className="w-full max-w-[320px] md:w-auto" />
        </Link>
        <RiskLine className="mt-6" />
      </Reveal>
    </SeoSection>
  );
}

export function RiskLine({ className = '' }: { className?: string }) {
  return (
    <View className={`flex-row flex-wrap justify-center gap-x-5 gap-y-2 ${className}`}>
      {['Grátis para começar', 'Sem cartão', 'Cancela quando quiser'].map((label) => (
        <View key={label} className="flex-row items-center gap-1.5">
          <Ionicons name="checkmark-circle" size={16} color="#FD5001" />
          <ThemedText className="text-sm text-neutral-500">{label}</ThemedText>
        </View>
      ))}
    </View>
  );
}

/** JSON-LD `BreadcrumbList`: `path` relativo ao site (`/guias`). */
export const breadcrumbJsonLd = (siteUrl: string, items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${siteUrl}${item.path}`,
  })),
});
